/**
 * Chat storage — Force cleanup for ONE target (#758 part 2).
 *
 * ── Confirm books a deletion; it does not perform one ─────────────────
 *
 * The admin chooses two numbers: which conversations (idle longer than X)
 * and when it happens (Y days from now). The member is told the moment it
 * is booked, and those days are the feature — they are when somebody can
 * save or star what matters. "Runs now" exists, and the footer turns red
 * and asks again, because at zero nobody gets any notice at all.
 *
 * ── Why the preview is two sentences, not one ─────────────────────────
 *
 * "7 conversations · 320 MB of files" is what goes. "Their usage will drop
 * to 500 MB of 1 GB" is what the member will see afterwards, and it is the
 * one the admin is actually deciding about — they opened this because
 * somebody is near a limit. A figure without the resulting state makes
 * them do that subtraction in their head.
 *
 * ── Two honest approximations, both stated on screen ──────────────────
 *
 * The byte figure is an UPPER BOUND: the quota deduplicates a file shared
 * with another conversation or present in the Drive, and the preview
 * cannot without re-running the five-way quota SQL per conversation.
 * Overstating is the safe direction for a delete confirmation. And the
 * projection is "up to" for the same reason — their usage may drop by
 * less.
 *
 * ── The starred checkbox defaults OFF and says why ────────────────────
 *
 * A star is the member's own "keep this". An admin can override it; that
 * is a deliberate act, not a default, and the helper line says so rather
 * than leaving the box looking like an option nobody thought about.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { storageApi, type ChatStorageCleanupResult, type MemberChatStorageRow } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { readPreview, readBooking, singleConfirmArmed } from './cleanupVerdict'


const DELAY_UNITS = [
  { key: 'hours', minutes: 60 },
  { key: 'days', minutes: 60 * 24 },
  { key: 'weeks', minutes: 60 * 24 * 7 },
] as const

/** Under a day is not much notice. Not refused — an admin may have a good
 *  reason — but said out loud, because the days are the whole point of
 *  booking rather than deleting, and a number typed in a hurry is exactly
 *  how somebody loses work they would have saved. */
const SHORT_NOTICE_MINUTES = 24 * 60

/** The window, typed rather than chosen (Season 2026-10-17 — the agreed
 *  artboard had a dropdown; a free field covers the ones it did not list). */
const MIN_IDLE_DAYS = 1
const MAX_IDLE_DAYS = 3650
function wholeIdleDays(v: string): number | null {
  const t = v.trim()
  if (t === '' || !/^\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) && n >= MIN_IDLE_DAYS && n <= MAX_IDLE_DAYS ? n : null
}

export function CleanUpChatStorageDialog({
  target, member, unattributedBytes, onClose, onBooked, onSeeActivity, onStop,
}: {
  target: 'member' | 'unattributed'
  /** Present for a member target: their figure feeds the subtitle and the
   *  "will drop to" projection, which is the decision the admin is making. */
  member?: MemberChatStorageRow
  /** Present for the unattributed target, which has no owner and no limit. */
  unattributedBytes?: number
  onClose: () => void
  onBooked: () => void
  /** Open Cleanup activity — where a cleanup that started now is watched
   *  (D35: this dialog can no longer say what it deleted). */
  onSeeActivity?: () => void
  /** Stop the cleanup this dialog just started (D35 step ④). */
  onStop?: (id: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const [idleText, setIdleText] = useState('30')
  const [includeStarred, setIncludeStarred] = useState(false)
  const [delayAmount, setDelayAmount] = useState('7')
  const [delayUnit, setDelayUnit] = useState<(typeof DELAY_UNITS)[number]['key']>('days')
  const idleDays = wholeIdleDays(idleText)
  const [preview, setPreview] = useState<{ result: ChatStorageCleanupResult; forKey: string } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<ChatStorageCleanupResult | null>(null)
  /** The second confirmation the zero-delay footer opens. */
  const [confirmingNow, setConfirmingNow] = useState(false)

  const unit = DELAY_UNITS.find((u) => u.key === delayUnit)!
  const amount = /^\d+$/.test(delayAmount.trim()) ? Number(delayAmount.trim()) : null
  const runInMinutes = amount === null ? null : amount * unit.minutes
  const validDelay = runInMinutes !== null && runInMinutes >= 0 && runInMinutes <= 365 * 24 * 60
  const runsNow = runInMinutes === 0
  /** Short, but not nothing — a different warning from "runs now". */
  const shortNotice = runInMinutes !== null && runInMinutes > 0 && runInMinutes < SHORT_NOTICE_MINUTES

  const name = target === 'member'
    ? (member?.email.split('@')[0] ?? '')
    : t('settings.storage.chatStorageUnattributed')

  // The preview is keyed on EVERY input that changes what would go, so an
  // answer can never be shown beside a setting it did not answer for. The
  // first cut keyed on nothing and showed the previous window's count on
  // the button that applied the new one.
  const key = `${idleDays}:${includeStarred}`
  const lastKey = useRef(key)
  // Bumped when something invalidates the preview WITHOUT changing the
  // window — a refusal that proves the count on screen is stale. Without
  // it, clearing the preview left the box on its "Checking…" arm for
  // ever, because the effect below keys on the window alone.
  const [reloadNonce, setReloadNonce] = useState(0)
  const seq = useRef(0)
  const runPreview = useCallback(async (forKey: string, days: number, starred: boolean, keepFailure = false) => {
    const mine = ++seq.current
    setPreviewing(true)
    // ⚠️ A REFETCH CAUSED BY A REFUSAL MUST NOT ERASE THE REFUSAL. The
    // nonce below re-runs this 300 ms after the server said "could not
    // read that" or "nothing to clear", and clearing unconditionally
    // made the message flash and vanish — leaving a fresh preview, an
    // armed button, and no explanation of why nothing was booked. The
    // admin presses again and gets the same 300 ms flash.
    if (!keepFailure) setFailed(null)
    try {
      const r = await storageApi.cleanUpChatStorage({
        target, email: member?.email, idleDays: days, includeStarred: starred,
      })
      if (seq.current === mine) setPreview({ result: r, forKey })
    } catch {
      if (seq.current === mine) { setPreview(null); setFailed('preview') }
    } finally {
      if (seq.current === mine) setPreviewing(false)
    }
  }, [target, member?.email])

  useEffect(() => {
    // An incomplete or out-of-range window has nothing to preview, and
    // asking the server about it would answer for a number the admin is
    // still typing.
    if (idleDays === null) { setPreview(null); return }

    // Debounced: this walks every workspace's database, so a preview per
    // keystroke would have the server scanning the organisation repeatedly
    // while one person adjusts a number.
    // A NEW WINDOW IS A NEW QUESTION, so a refusal about the old one
    // stops applying — only a refetch caused by the nonce keeps it.
    const keepFailure = lastKey.current === key && reloadNonce > 0
    lastKey.current = key
    const id = setTimeout(() => { void runPreview(key, idleDays, includeStarred, keepFailure) }, 300)
    return () => clearTimeout(id)
  }, [key, idleDays, includeStarred, runPreview, reloadNonce])

  /** Only while it still answers for what is on screen. */
  const current = preview && preview.forKey === key ? preview.result : null

  /**
   * The shared reading (D32) — what Confirm acts on. A zero the scan could
   * not prove (`unproven`) still arms it: the server's own refusal is the
   * honest answer there, not a guess made here. The preview box below is
   * drawn from it too.
   */
  const reading = readPreview(current)

  const book = async () => {
    if (!validDelay || idleDays === null) return
    setBusy(true)
    setFailed(null)
    try {
      const r = await storageApi.cleanUpChatStorage({
        target, email: member?.email, idleDays: idleDays!, includeStarred, apply: true, runInMinutes: runInMinutes!,
      })
      setDone(r)
      onBooked()
    } catch (e) {
      // TWO refusals are not failures, and saying "try again" about
      // either of them sends an admin round a loop that cannot end.
      //
      //  * ALREADY_SCHEDULED — the cure is on the row behind this dialog.
      //  * NOTHING_TO_DO — the server refuses an empty booking (it would
      //    notify the member that their conversations are about to be
      //    cleared, clear nothing, and hold their one live slot until the
      //    run date). Reachable from here only by a race: the preview
      //    said 3, and the member starred all three while the admin was
      //    picking a date. "Could not book, please try again" would be
      //    both wrong and unachievable.
      const code = (e as { code?: string })?.code
      setFailed(
        // ⚠️ TWO CLASHES, OPPOSITE ADVICE. "Cancel it from their row
        // first" is the cure for a BOOKING. A row that is already
        // RUNNING cannot be cancelled at all — the row's own menu says
        // so — so sending the admin there was sending them to a button
        // the product deliberately does not offer (round 12, finding 2).
        code === 'CLEANUP_ALREADY_RUNNING' ? 'clashRunning'
          : code === 'CLEANUP_STOPPING' ? 'clashStopping'
          : code === 'CLEANUP_ALREADY_SCHEDULED' ? 'clash'
          : code === 'CLEANUP_NOTHING_TO_DO' ? 'nothing'
          : code === 'CLEANUP_INCOMPLETE_SCAN' ? 'incomplete'
          : 'book')
      // EITHER REFUSAL PROVES THE PREVIEW ON SCREEN IS STALE — it is why
      // the server refused. Leaving it would show "will clear 3" and
      // "nothing has been idle that long" at the same time, with Confirm
      // still armed on the stale 3 and every press repeating the pair.
      //
      // Dropping it is not enough on its own: the effect keys on the
      // window, which has not changed, so nothing refetches and the box
      // sits on "Checking what that would clear…" for ever — a spinner
      // that never resolves, which is worse than the stale number it
      // replaced. The nonce is what actually asks again.
      if (code === 'CLEANUP_NOTHING_TO_DO' || code === 'CLEANUP_INCOMPLETE_SCAN') {
        setPreview(null)
        setReloadNonce((n) => n + 1)
      }
    } finally {
      setBusy(false)
      setConfirmingNow(false)
    }
  }

  const runsAt = runInMinutes === null ? null : new Date(Date.now() + runInMinutes * 60 * 1000)
  const usedNow = target === 'member' ? (member?.totalBytes ?? 0) : (unattributedBytes ?? 0)
  const limit = target === 'member' ? member?.limitBytes ?? null : null
  const after = Math.max(0, usedNow - (current?.bytes ?? 0))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className="relative z-10 max-w-xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in">

        <div className="px-6 pt-5 pb-3.5 themed-border border-b">
          <h3 className="text-[17px] font-semibold themed-text">
            {t('settings.storage.cleanupTitle', { name })}
          </h3>
          <p className="text-xs themed-text-muted mt-1 leading-relaxed">
            {/* The usage sentence only where there IS a usage and a limit:
                the unattributed row has neither, and inventing one would be
                the sort of confident wrong number this page is careful
                about everywhere else. */}
            {target === 'member' && limit !== null
              ? `${t('settings.storage.cleanupUsingNow', { used: formatBytes(usedNow), limit: formatBytes(limit) })} `
              : ''}
            {t('settings.storage.cleanupSubtitle')}
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto min-h-0 flex-1 space-y-4">
          {done ? (
            <div className="space-y-2">
              {done.startsNow ? (
                // ⚠️ STARTED, NOT CLEARED (D35). Nothing has been deleted
                // yet: the worker takes it within a minute and clears the
                // whole list in passes. The count is what it will work
                // through, and what it actually did is in Cleanup activity
                // — this dialog cannot know it.
                <p className="text-sm themed-text">
                  {t('settings.storage.cleanupStartedCount', { count: done.conversations ?? 0 })}
                </p>
              ) : (
                <p className="text-sm themed-text">
                  {t('settings.storage.cleanupBookedCount', {
                    count: done.conversations ?? 0, when: formatWhen(done.runAt),
                  })}
                </p>
              )}
              {done.startsNow && done.id && onStop && (
                <p className="text-[13px] themed-text-muted">{t('settings.storage.cleanupStartedStoppable')}</p>
              )}
              {/* Every target but the unattributed one has somebody to
                  tell — a start-now as much as a booking (D35: they are
                  told now, and again when the conversations go). */}
              {target === 'member' && (
                <p className="text-[13px] themed-text-muted">
                  {done.notified
                    ? t('settings.storage.cleanupBookedNotified', { name })
                    // Said plainly rather than hidden: the whole value of
                    // booking ahead is the warning, so a warning that did
                    // not arrive is the thing the admin needs to know.
                    : t('settings.storage.cleanupBookedNotTold', { name })}
                </p>
              )}
              {readBooking(done).truncated && (
                // The list hit what one booking can freeze — the done screen
                // replaces the preview box that said so (D32: the bulk
                // dialog never said it at all).
                <p className="text-[13px] text-amber-500">{t('settings.storage.cleanupMoreLeft')}</p>
              )}
              {readBooking(done).partial && (
                // ⚠️ THE REASON A BOOKING CAN BE THINNER THAN EXPECTED.
                // The preview box says this too, but the done view
                // REPLACES that box.
                <p className="text-[13px] text-amber-500">
                  {t('settings.storage.cleanupSkipped', { count: done.workspacesSkipped })}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-semibold themed-text" htmlFor="cleanup-idle">
                  {t('settings.storage.cleanupIdleLabel')}
                </label>
                <input
                  id="cleanup-idle"
                  type="text"
                  inputMode="numeric"
                  value={idleText}
                  onChange={(e) => setIdleText(e.target.value)}
                  className="w-20 text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                />
                <span className="text-sm themed-text-muted">{t('settings.storage.cleanupUnit_days')}</span>
                {idleDays === null && (
                  <span className="text-[12px] text-rose-400">
                    {t('settings.storage.cleanupIdleInvalid', { min: MIN_IDLE_DAYS, max: MAX_IDLE_DAYS })}
                  </span>
                )}
              </div>

              <div className="flex items-start gap-2.5">
                <input
                  id="cleanup-starred"
                  type="checkbox"
                  checked={includeStarred}
                  onChange={(e) => setIncludeStarred(e.target.checked)}
                  className="mt-0.5"
                />
                <label htmlFor="cleanup-starred" className="text-[13px] themed-text">
                  {t(target === 'member' ? 'settings.storage.cleanupIncludeStarred' : 'settings.storage.cleanupIncludeStarredAny')}
                  <span className="block text-xs themed-text-muted mt-0.5">
                    {t('settings.storage.cleanupStarredHint')}
                  </span>
                </label>
              </div>

              <div className="rounded-xl themed-bg-tertiary px-4 py-3 space-y-1">
                {/* DRAWN FROM THE SHARED READING (D32) — the same one the
                    bulk dialog draws each member's line from, so the two
                    can no longer say different things about one answer. */}
                {previewing || reading === null ? (
                  <p className="text-[13px] themed-text-muted">
                    {failed === 'preview'
                      ? t('settings.storage.cleanupPreviewFailed')
                      : t('settings.storage.cleanupChecking')}
                  </p>
                ) : reading.kind === 'nothing' ? (
                  <>
                    <p className="text-[13px] themed-text-muted">{t('settings.storage.cleanupNothing')}</p>
                    {/* ALSO on the zero branch. "Nothing has been idle that
                        long" and "nothing matched in the batch I looked at"
                        are different statements, and the second is what a
                        truncated scan found. */}
                    {reading.truncated && (
                      <p className="text-[12px] text-amber-500">{t('settings.storage.cleanupMoreLeft')}</p>
                    )}
                  </>
                ) : reading.kind === 'unproven' ? (
                  // ⚠️ A ZERO NOBODY COULD PROVE. This box said "nothing has
                  // been idle that long" and, under it, "a workspace could not
                  // be read" — two sentences contradicting each other. The
                  // bulk dialog said "could not check"; this one now says nothing was
                  // found in what COULD be read, and the line below says what
                  // could not (D32).
                  <p className="text-[13px] text-amber-500">{t('settings.storage.cleanupUnprovenZero')}</p>
                ) : reading.kind === 'failed' ? null : (
                  <>
                    {/* THE NUMBER IS WHAT HAPPENS, now or later (D35). Until
                        D35 "run now" was one 200-conversation pass and this
                        box had to say so; it is a booking now, worked
                        through to the end in batches. */}
                    <p className="text-[15px] themed-text">
                      {t('settings.storage.cleanupWillClear')}{' '}
                      <strong>{t('settings.storage.cleanupConversations', { count: reading.conversations })}</strong>
                      {' · '}
                      <strong>{t('settings.storage.cleanupOfFiles', { size: formatBytes(reading.bytes) })}</strong>
                    </p>
                    {runsNow && (
                      <p className="text-[13px] themed-text-muted">{t('settings.storage.cleanupAllInBatches')}</p>
                    )}
                    {target === 'member' && limit !== null && (
                      <p className="text-[13px] themed-text-muted">
                        {t('settings.storage.cleanupWillDropTo', {
                          after: formatBytes(after), limit: formatBytes(limit),
                        })}
                      </p>
                    )}
                    {reading.truncated && (
                      <p className="text-[12px] text-amber-500">{t('settings.storage.cleanupMoreLeft')}</p>
                    )}
                  </>
                )}
                {reading && (reading.kind === 'unproven' || reading.kind === 'partial') && (
                  <p className="text-[12px] text-amber-500">
                    {t('settings.storage.cleanupSkipped', { count: reading.unreadable })}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold themed-text mb-2">{t('settings.storage.cleanupWhenLabel')}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={delayAmount}
                    onChange={(e) => setDelayAmount(e.target.value)}
                    aria-label={t('settings.storage.cleanupWhenLabel')}
                    className="w-20 text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  />
                  <select
                    value={delayUnit}
                    onChange={(e) => setDelayUnit(e.target.value as typeof delayUnit)}
                    aria-label={t('settings.storage.cleanupWhenUnit')}
                    className="text-sm px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  >
                    {DELAY_UNITS.map((u) => (
                      <option key={u.key} value={u.key}>{t(`settings.storage.cleanupUnit_${u.key}`)}</option>
                    ))}
                  </select>
                  <span className="text-sm themed-text-muted">{t('settings.storage.cleanupFromNow')}</span>
                </div>
                {!validDelay && (
                  <p className="text-[12px] text-rose-400 mt-1.5">{t('settings.storage.cleanupWhenInvalid')}</p>
                )}
              </div>

              {validDelay && (
                <div className={`rounded-xl px-4 py-3 ${runsNow ? 'border border-rose-500/40 bg-rose-500/10' : 'themed-bg-tertiary'}`}>
                  {runsNow ? (
                    <>
                      {/* THE footer the design draws in red. Zero is not a
                          shorter delay, it is the absence of the warning
                          the whole feature is built around, so it reads as
                          a different thing rather than as "0 days". */}
                      <p className="text-[13px] text-rose-400 font-semibold">
                        {t('settings.storage.cleanupRunsNow')}
                      </p>
                      <p className="text-xs themed-text-muted mt-0.5">{t('settings.storage.cleanupRunsNowHint')}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[13px] themed-text">
                        {t('settings.storage.cleanupRunsAt', { when: formatWhen(runsAt?.toISOString()) })}
                      </p>
                      <p className="text-xs themed-text-muted mt-0.5">
                        {t('settings.storage.cleanupNotifiedNow', { name })}
                      </p>
                      {/* UNDER A DAY. Not refused — an admin may have a
                          good reason — but the point of booking rather
                          than deleting is the time it gives somebody, and
                          a few hours is not much of it. */}
                      {shortNotice && (
                        <p className="text-xs text-amber-500 mt-1">{t('settings.storage.cleanupShortNotice')}</p>
                      )}
                      {/* ⚠️ WHICH WAY THE NUMBER CAN MOVE (D31). The
                          booking freezes its list, so the count above is
                          a CEILING rather than a guess about the run
                          date — it was a guess, and an admin shown "3"
                          could get 63 as the 23-to-30-day band crossed
                          the line during the notice week. Said here
                          because it also tells the admin what the member
                          can do about it: star something, unless this
                          cleanup overrides stars, in which case they
                          have no lever and should not be left trying. */}
                      <p className="text-xs themed-text-muted mt-1">
                        {/* ⚠️ THE UNATTRIBUTED TARGET IS NOT A PERSON.
                            `name` falls back to "Uploader unknown" for
                            it, which rendered "a conversation Uploader
                            unknown stars is taken off it" — and worse
                            than reading oddly, it is untrue: those files
                            have nobody who could star anything, so for
                            that target the list really is final. The
                            dialog already splits this case for the
                            include-starred checkbox. */}
                        {target !== 'member'
                          ? t('settings.storage.cleanupFrozenNoteUnattributed')
                          : t(includeStarred
                              ? 'settings.storage.cleanupFrozenNoteStarred'
                              : 'settings.storage.cleanupFrozenNote', { name })}
                      </p>
                    </>
                  )}
                </div>
              )}

              {failed === 'clash' && (
                <p className="text-[13px] text-rose-500">{t('settings.storage.cleanupClash')}</p>
              )}
              {failed === 'clashRunning' && (
                <p className="text-[13px] text-rose-500">{t('settings.storage.cleanupClashRunning')}</p>
              )}
              {failed === 'clashStopping' && (
                <p className="text-[13px] text-amber-500">{t('settings.storage.cleanupClashStopping')}</p>
              )}
              {failed === 'nothing' && (
                <p className="text-[13px] themed-text-muted">{t('settings.storage.cleanupNothing')}</p>
              )}
              {failed === 'incomplete' && (
                // Amber, not muted: unlike "nothing to clear" this one is
                // worth coming back for, and the cure is a minute's wait.
                <p className="text-[13px] text-amber-500">{t('settings.storage.cleanupIncompleteScan')}</p>
              )}
              {failed === 'book' && (
                <p className="text-[13px] text-rose-500">{t('settings.storage.cleanupBookFailed')}</p>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3.5 themed-border border-t flex items-center justify-end gap-2.5">
          {done?.startsNow && onSeeActivity && (
            // Where the result will be — this dialog cannot say it (D35).
            <button
              type="button"
              onClick={onSeeActivity}
              className={`text-[13px] px-4 py-2 rounded-lg themed-border border themed-text${done.id && onStop ? '' : ' mr-auto'}`}
            >
              {t('settings.storage.cleanupSeeActivity')}
            </button>
          )}
          {done?.startsNow && done.id && onStop && (
            // The design's "Stop it": an admin who started the wrong
            // window can take it back here, while it is still running.
            <button
              type="button"
              onClick={() => onStop(done.id!)}
              className="text-[13px] px-4 py-2 rounded-lg border border-rose-400/50 text-rose-400 font-semibold mr-auto"
            >
              {t('settings.storage.cleanupStopIt')}
            </button>
          )}
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t(done ? 'common:close' : 'common:cancel')}
          </button>
          {!done && (
            confirmingNow ? (
              // The second confirmation. Both options stay available —
              // the friction is the point, not a block.
              <>
                <span className="text-[13px] text-rose-400 mr-auto">{t('settings.storage.cleanupConfirmNowAsk')}</span>
                <button
                  type="button"
                  onClick={() => { void book() }}
                  disabled={busy}
                  className="text-[13px] px-4 py-2 rounded-lg bg-rose-700 text-white font-semibold disabled:opacity-60"
                >
                  {busy ? t('settings.storage.cleanupWorking') : t('settings.storage.cleanupConfirmNowYes')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { if (runsNow) setConfirmingNow(true); else void book() }}
                disabled={busy || previewing || !validDelay || idleDays === null || !singleConfirmArmed(reading)}
                className={`text-[13px] px-4 py-2 rounded-lg text-white font-semibold disabled:opacity-60 ${runsNow ? 'bg-rose-700' : 'bg-rose-600'}`}
              >
                {busy
                  ? t('settings.storage.cleanupWorking')
                  : runsNow
                    // The whole list — it is a booking now (D35).
                    ? t('settings.storage.cleanupClearNow', { count: current?.conversations ?? 0 })
                    : t('settings.storage.cleanupConfirm')}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  )
}

/** The booked moment, in the member's own terms. Locale-formatted rather
 *  than an ISO string: this date is the one thing the admin and the member
 *  both act on. */
function formatWhen(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
